import { Injectable } from '@angular/core';
import { EMPTY, Observable } from 'rxjs';
import { ApiInterface } from '../api-interface';
import { ApiService } from '../api.service';
import {
  DespesaAcessoriaConsultaDTO,
  DespesaAcessoriaDTO,
  DespesaAcessoriaContadorFiltroDTO,
  DespesaAcessoriaFiltroDTO,
  DespesaAcessoriaPendenteDTO,
  DespesaAcessoriaRelatorioPdfFiltroDTO,
  PageDespesaAcessoriaContadorDTO,
  PageDespesaAcessoriaDTO,
  PageDespesaAcessoriaHistoricoDTO,
} from '../../models/contador/despesa-acessoria.model';

/**
 * Service da Despesa Acessória do contrato do contador.
 *
 * Cobre o CRUD sob `/contador/contratos/{contratoId}/despesas` (contador-only,
 * resolvido pelo contador autenticado + contratoId no backend):
 * - GET    /contador/contratos/{contratoId}/despesas          (listar)
 * - POST   /contador/contratos/{contratoId}/despesas          (cadastrar, 201)
 * - PUT    /contador/contratos/{contratoId}/despesas/{id}     (editar)
 * - DELETE /contador/contratos/{contratoId}/despesas/{id}     (excluir soft, 204)
 *
 * Implementa `ApiInterface` (padrão do projeto). Os métodos genéricos delegam
 * para o `ApiService`; os métodos específicos montam a URL a partir do
 * contratoId.
 */
@Injectable({ providedIn: 'root' })
export class DespesaAcessoriaService implements ApiInterface {
  constructor(private apiService: ApiService) {}

  // ---------------------------------------------------------------------------
  // ApiInterface
  // ---------------------------------------------------------------------------

  get<T>(url: string, data?: any): Observable<PageDespesaAcessoriaDTO> {
    const queryParams = this.apiService.buildQueryParams(data);
    const path = queryParams ? `${url}?${queryParams}` : url;
    return this.apiService.read<PageDespesaAcessoriaDTO>(path);
  }

  post<T>(url: string, data: DespesaAcessoriaDTO): Observable<DespesaAcessoriaConsultaDTO> {
    return this.apiService.create<DespesaAcessoriaConsultaDTO>(url, data);
  }

  put<T>(url: string, data: DespesaAcessoriaDTO): Observable<DespesaAcessoriaConsultaDTO> {
    return this.apiService.update<DespesaAcessoriaConsultaDTO>(url, data);
  }

  delete<T>(url: string): Observable<any> {
    return this.apiService.delete(url);
  }

  // ---------------------------------------------------------------------------
  // Métodos específicos da feature
  // ---------------------------------------------------------------------------

  /** URL base das despesas de um contrato. */
  private baseUrl(contratoId: number): string {
    return `/contador/contratos/${contratoId}/despesas`;
  }

  /**
   * GET — lista (paginada) as despesas acessórias do contrato (inclui CANCELADA).
   *
   * O backend agora responde um envelope Spring `Page<DespesaAcessoriaConsultaDTO>`.
   * Parâmetros de paginação/ordenação (formato Spring):
   * - `page`: índice da página (0-based);
   * - `size`: itens por página (default do backend: 20);
   * - `sort`: `campo,direcao` (default do backend: `dataPagamento,desc`); campos
   *   aceitos: `valor`, `dataPagamento`, `tipoDespesa`, `status`.
   *
   * Filtros OPCIONAIS (`DespesaAcessoriaFiltroDTO`), todos combináveis:
   * - `de`/`ate`: período por `dataPagamento` (ISO `yyyy-MM-dd`);
   * - `tipoDespesa`: nome do enum `TipoDespesa`;
   * - `status`: situação da despesa;
   * - `cobranca`: `A_COBRAR` | `COBRADO`.
   *
   * `buildQueryParams` descarta chaves `undefined`, então omitir `sort`/filtros
   * deixa o backend aplicar o default (sem restrição).
   */
  listar(
    contratoId: number,
    page: number = 0,
    size: number = 20,
    sort?: string,
    filtro?: DespesaAcessoriaFiltroDTO,
  ): Observable<PageDespesaAcessoriaDTO> {
    return this.get(this.baseUrl(contratoId), { page, size, sort, ...(filtro || {}) });
  }

  /**
   * GET — listagem CONSOLIDADA do contador (`/contador/despesas`): as despesas de
   * TODOS os contratos numa lista só, para a aba "Gastos acessórios" da Área do
   * Contador. Cada item traz o cliente (indicado) resolvido pelo backend.
   *
   * Paginação/ordenação no formato Spring (`sort` default do backend:
   * `dataPagamento,desc`); filtros opcionais em `DespesaAcessoriaContadorFiltroDTO`
   * (indicado, situação e período por `dataPagamento`).
   */
  listarDoContador(
    page: number = 0,
    size: number = 20,
    sort?: string,
    filtro?: DespesaAcessoriaContadorFiltroDTO,
  ): Observable<PageDespesaAcessoriaContadorDTO> {
    const queryParams = this.apiService.buildQueryParams({ page, size, sort, ...(filtro || {}) });
    const path = queryParams ? `/contador/despesas?${queryParams}` : '/contador/despesas';
    return this.apiService.read<PageDespesaAcessoriaContadorDTO>(path);
  }

  /**
   * GET `/contador/despesas/relatorio-pdf` — PDF (Base64) dos gastos acessórios do
   * contador conforme os filtros da aba. Contador-only no backend (403 quando a
   * empresa não é contadora).
   *
   * A resposta é `text/plain` com o Base64 PURO do PDF, por isso usamos
   * `apiService.readText` (NUNCA `read`, que faria JSON.parse e quebraria o Base64;
   * NUNCA `readBlob`/`application/pdf`). `buildQueryParams` descarta chaves
   * `undefined`, então só os filtros preenchidos vão na query; `page`/`size` NUNCA
   * vão (não existem no tipo `DespesaAcessoriaRelatorioPdfFiltroDTO`).
   *
   * O cast `filtro as any` é necessário porque `ApiQueryParamsDTO` só declara
   * `page`/`size`/`sort`/`filter` (mesmo padrão de `gerarRelatorioComissoesPdf`).
   * Ver `vendas/docs/api/API_CONTRACT_export-pdf-gastos-acessorios.md`.
   */
  gerarRelatorioDespesasPdf(
    filtro: DespesaAcessoriaRelatorioPdfFiltroDTO,
  ): Observable<string> {
    const queryParams = this.apiService.buildQueryParams(filtro as any);
    const path = queryParams
      ? `/contador/despesas/relatorio-pdf?${queryParams}`
      : '/contador/despesas/relatorio-pdf';
    return this.apiService.readText<string>(path);
  }

  /**
   * GET `/contador/despesas/relatorio-export?...&formato=csv|xlsx` — baixa os gastos
   * acessórios do contador como arquivo binário (.csv/.xlsx) com os MESMOS filtros do
   * PDF. Corpo binário via `readBlob`; body vazio (blob.size === 0) sinaliza "sem
   * dados" (tratado no componente). Contador-only no backend (403); 422 formato
   * inválido/teto de 5000. `buildQueryParams` descarta chaves `undefined`; o cast
   * `as any` é necessário porque `ApiQueryParamsDTO` só declara page/size/sort/filter
   * (mesmo padrão de `gerarRelatorioComissoesArquivo`). O nome do arquivo baixado é
   * definido no FE (não usa Content-Disposition).
   * Ver `vendas/docs/api/API_CONTRACT_export-csv-xlsx-gastos-acessorios.md`.
   */
  gerarRelatorioDespesasArquivo(
    formato: 'csv' | 'xlsx',
    filtro: DespesaAcessoriaRelatorioPdfFiltroDTO,
  ): Observable<Blob> {
    const queryParams = this.apiService.buildQueryParams({ ...filtro, formato } as any);
    return this.apiService.readBlob(`/contador/despesas/relatorio-export?${queryParams}`);
  }

  /** POST — cadastra uma despesa acessória no contrato. */
  cadastrar(
    contratoId: number,
    dto: DespesaAcessoriaDTO,
  ): Observable<DespesaAcessoriaConsultaDTO> {
    return this.post(this.baseUrl(contratoId), dto);
  }

  /** PUT — edita a despesa acessória informada. */
  editar(
    contratoId: number,
    id: number,
    dto: DespesaAcessoriaDTO,
  ): Observable<DespesaAcessoriaConsultaDTO> {
    return this.put(`${this.baseUrl(contratoId)}/${id}`, dto);
  }

  /** DELETE — exclui (soft, status CANCELADA) a despesa acessória informada. */
  excluir(contratoId: number, id: number): Observable<any> {
    if (contratoId == null || id == null) {
      return EMPTY;
    }
    return this.delete(`${this.baseUrl(contratoId)}/${id}`);
  }

  // ---------------------------------------------------------------------------
  // Cliente — aprovação das despesas pendentes
  //   Endpoints sob `/cliente/despesas-contador` (cliente autenticado):
  //   - GET   /pendentes         (lista as despesas pendentes de aprovação)
  //   - PATCH /{id}/aprovar      (aprova -> gera os lançamentos, ATIVA)
  //   - PATCH /{id}/recusar      (recusa -> RECUSADA)
  // ---------------------------------------------------------------------------

  /** URL base das despesas do contador na área do cliente. */
  private clienteBaseUrl(): string {
    return '/cliente/despesas-contador';
  }

  /** GET — lista as despesas pendentes de aprovação do cliente autenticado. */
  listarPendentes(): Observable<DespesaAcessoriaPendenteDTO[]> {
    return this.apiService.read<DespesaAcessoriaPendenteDTO[]>(
      `${this.clienteBaseUrl()}/pendentes`,
    );
  }

  /**
   * GET — histórico (paginado) das despesas do contador JÁ DECIDIDAS pelo
   * cliente (aprovadas/recusadas). O backend responde `Page<...>` (paginação e
   * ordenação no servidor):
   * - `page`: índice 0-based; `size`: itens por página (default backend: 20);
   * - `sort`: `campo,direcao` (default backend: `dataDecisao,desc`); whitelist:
   *   `valor`, `dataPagamento`, `tipoDespesa`, `status`, `dataDecisao`, `pagoEm`.
   * `buildQueryParams` descarta `undefined`, então omitir `sort` usa o default.
   */
  listarHistorico(
    page: number = 0,
    size: number = 20,
    sort?: string,
  ): Observable<PageDespesaAcessoriaHistoricoDTO> {
    const queryParams = this.apiService.buildQueryParams({ page, size, sort });
    const path = queryParams
      ? `${this.clienteBaseUrl()}/historico?${queryParams}`
      : `${this.clienteBaseUrl()}/historico`;
    return this.apiService.read<PageDespesaAcessoriaHistoricoDTO>(path);
  }

  /** PATCH — aprova a despesa (gera os lançamentos). */
  aprovar(id: number): Observable<DespesaAcessoriaConsultaDTO> {
    if (id == null) {
      return EMPTY;
    }
    return this.apiService.patch<DespesaAcessoriaConsultaDTO>(
      `${this.clienteBaseUrl()}/${id}/aprovar`,
      {},
    );
  }

  /** PATCH — recusa a despesa. */
  recusar(id: number): Observable<DespesaAcessoriaConsultaDTO> {
    if (id == null) {
      return EMPTY;
    }
    return this.apiService.patch<DespesaAcessoriaConsultaDTO>(
      `${this.clienteBaseUrl()}/${id}/recusar`,
      {},
    );
  }
}
